import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import { ApiNftLinkMediaPreviewStatusEnum } from "@/generated/models/ApiNftLinkMediaPreview";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { SingleWaveDropPosition } from "@/components/waves/drop/SingleWaveDropPosition";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import Link from "next/link";
import React, { useMemo } from "react";
import { Tooltip } from "react-tooltip";
import MyStreamWaveMyVoteInput from "./MyStreamWaveMyVoteInput";
import MyStreamWaveMyVoteVotes from "./MyStreamWaveMyVoteVotes";
interface MyStreamWaveMyVoteProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly isChecked?: boolean | undefined;
  readonly onToggleCheck?: ((dropId: string) => void) | undefined;
  readonly isResetting?: boolean | undefined;
}
type ResolvedPreviewMedia = {
  readonly url: string;
  readonly mimeType: string;
};
const DEFAULT_MIME_TYPE = "image/jpeg";
const MIME_BY_EXTENSION: Record<string, string> = {
  avif: "image/avif",
  gif: "image/gif",
  jpeg: DEFAULT_MIME_TYPE,
  jpg: DEFAULT_MIME_TYPE,
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  ogg: "audio/ogg",
  ogv: "video/ogg",
  flac: "audio/flac",
  png: "image/png",
  svg: "image/svg+xml",
  wav: "audio/wav",
  webm: "video/webm",
  webp: "image/webp",
  glb: "model/gltf-binary",
  gltf: "model/gltf+json",
  usdz: "model/vnd.usdz"
};
const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
const inferMimeTypeFromUrl = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    const extensionMatch = parsed.pathname.toLowerCase().match(/\.([a-z0-9]+)$/);
    if (!extensionMatch?.[1]) {
      return undefined;
    }
    return MIME_BY_EXTENSION[extensionMatch[1]];
  } catch {
    const path = url.split("?")[0]?.toLowerCase() ?? "";
    const extensionMatch = path.match(/\.([a-z0-9]+)$/);
    if (!extensionMatch?.[1]) {
      return undefined;
    }
    return MIME_BY_EXTENSION[extensionMatch[1]];
  }
};
const resolveCurationPreviewMedia = (nftLinks: ExtendedDrop["nft_links"]): ResolvedPreviewMedia | null => {
  if (typeof nftLinks?.length !== "number" || nftLinks.length === 0) {
    return null;
  }
  for (const nftLink of nftLinks) {
    const preview = nftLink.data?.media_preview;
    const previewMimeType = toNonEmptyString(preview?.mime_type);
    const previewUrl = preview?.status === ApiNftLinkMediaPreviewStatusEnum.Ready ? toNonEmptyString(preview.card_url) ?? toNonEmptyString(preview.small_url) ?? toNonEmptyString(preview.thumb_url) : null;
    if (previewUrl) {
      return {
        url: previewUrl,
        mimeType: previewMimeType ?? inferMimeTypeFromUrl(previewUrl) ?? DEFAULT_MIME_TYPE
      };
    }
    const fallbackUrl = toNonEmptyString(nftLink.data?.media_uri);
    if (!fallbackUrl) {
      continue;
    }
    return {
      url: fallbackUrl,
      mimeType: inferMimeTypeFromUrl(fallbackUrl) ?? previewMimeType ?? DEFAULT_MIME_TYPE
    };
  }
  return null;
};
const MyStreamWaveMyVote: React.FC<MyStreamWaveMyVoteProps> = t0 => {
  const $ = _c(101);
  const {
    drop,
    onDropClick,
    isChecked: t1,
    onToggleCheck,
    isResetting: t2
  } = t0;
  const isChecked = t1 === undefined ? false : t1;
  const isResetting = t2 === undefined ? false : t2;
  const {
    isCurationWave
  } = useSeizeSettings();
  let t3;
  if ($[0] !== drop.parts) {
    t3 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const artWork = t3;
  let t4;
  if ($[2] !== drop.metadata) {
    t4 = getDropPreviewImageUrl(drop.metadata);
    $[2] = drop.metadata;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const previewImageUrl = t4;
  let t5;
  if ($[4] !== artWork || $[5] !== drop.nft_links || $[6] !== drop.wave.id || $[7] !== isCurationWave) {
    t5 = !artWork && isCurationWave(drop.wave.id.toLowerCase()) ? resolveCurationPreviewMedia(drop.nft_links) : null;
    $[4] = artWork;
    $[5] = drop.nft_links;
    $[6] = drop.wave.id;
    $[7] = isCurationWave;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const curationPreviewMedia = t5;
  const resolvedMediaUrl = artWork?.url ?? curationPreviewMedia?.url ?? null;
  const resolvedMediaMimeType = artWork?.mime_type ?? curationPreviewMedia?.mimeType ?? DEFAULT_MIME_TYPE;
  const badgeMimeType = artWork?.mime_type ?? curationPreviewMedia?.mimeType;
  let t6;
  if ($[9] !== drop || $[10] !== onDropClick) {
    t6 = () => {
      if (window.getSelection()?.toString()) {
        return;
      }
      onDropClick(drop);
    };
    $[9] = drop;
    $[10] = onDropClick;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleClick = t6;
  let t7;
  if ($[12] !== drop.id || $[13] !== onToggleCheck) {
    t7 = e => {
      e.stopPropagation();
      if (onToggleCheck) {
        onToggleCheck(drop.id);
      }
    };
    $[12] = drop.id;
    $[13] = onToggleCheck;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleCheckboxClick = t7;
  const t8 = drop.id;
  const t9 = `tw-cursor-pointer tw-rounded-xl tw-border tw-border-solid tw-bg-iron-950 tw-px-5 tw-py-4 tw-shadow-md tw-transition-all tw-duration-300 desktop-hover:hover:tw-shadow-lg ${isChecked ? "tw-border-primary-400" : "tw-border-iron-800 desktop-hover:hover:tw-border-iron-700"}`;
  const t10 = `tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid ${isChecked ? "tw-border-primary-400 tw-bg-primary-400/20" : "tw-border-iron-600 tw-bg-iron-800"} tw-cursor-pointer tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-shadow-md`;
  let t11;
  if ($[15] !== isChecked) {
    t11 = isChecked && <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="tw-size-4 tw-text-primary-400"><path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 0 1 1.04-.208Z" clipRule="evenodd" /></svg>;
    $[15] = isChecked;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== handleCheckboxClick || $[21] !== t12) {
    t13 = <div className="tw-mr-1 tw-flex-shrink-0 tw-self-start" onClick={handleCheckboxClick}>{t12}</div>;
    $[20] = handleCheckboxClick;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== previewImageUrl || $[24] !== resolvedMediaMimeType || $[25] !== resolvedMediaUrl) {
    t14 = resolvedMediaUrl && <MediaDisplay media_mime_type={resolvedMediaMimeType} media_url={resolvedMediaUrl} imageScale={ImageScale.AUTOx450} previewImageUrl={previewImageUrl} disableMediaInteraction={true} />;
    $[23] = previewImageUrl;
    $[24] = resolvedMediaMimeType;
    $[25] = resolvedMediaUrl;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <div className="tw-relative tw-h-56 tw-w-full tw-flex-shrink-0 tw-overflow-hidden tw-bg-iron-800 md:tw-size-[106px] md:tw-min-h-[106px] md:tw-min-w-[106px]"><div className="tw-relative tw-flex tw-h-full tw-w-full tw-transform tw-items-center tw-justify-center tw-duration-300 tw-ease-out desktop-hover:hover:tw-scale-105"><div className="tw-absolute tw-inset-0 tw-z-[1]">{t14}</div></div></div>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== badgeMimeType || $[30] !== drop.id) {
    t16 = <MediaTypeBadge mimeType={badgeMimeType} dropId={drop.id} size="sm" />;
    $[29] = badgeMimeType;
    $[30] = drop.id;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== drop.title) {
    t17 = <h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">{drop.title}</h3>;
    $[32] = drop.title;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t16 || $[35] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-gap-x-2">{t16}{t17}</div>;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== drop.rank) {
    t19 = typeof drop.rank === "number" && <SingleWaveDropPosition rank={drop.rank} />;
    $[37] = drop.rank;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18 || $[40] !== t19) {
    t20 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3">{t18}{t19}</div>;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== drop.author.pfp) {
    t21 = <div className="tw-relative tw-size-6 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10">{drop.author.pfp ? <img src={drop.author.pfp} alt="Profile" className="tw-h-full tw-w-full tw-bg-iron-800 tw-object-contain" /> : <div className="tw-h-full tw-w-full tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10" />}</div>;
    $[42] = drop.author.pfp;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = drop.author.handle ?? drop.author.id;
  const t23 = `/${drop.author.handle ?? drop.author.primary_address}`;
  let t24;
  if ($[44] !== drop.author.handle || $[45] !== drop.author.primary_address) {
    t24 = e_0 => {
      e_0.stopPropagation();
      e_0.preventDefault();
      window.open(`/${drop.author.handle ?? drop.author.primary_address}`, "_blank");
    };
    $[44] = drop.author.handle;
    $[45] = drop.author.primary_address;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== drop.author.handle || $[48] !== t23 || $[49] !== t24) {
    t25 = <Link href={t23} onClick={t24} className="tw-truncate tw-text-md tw-font-medium tw-text-iron-200 tw-no-underline tw-transition-colors tw-duration-200 desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{drop.author.handle}</Link>;
    $[47] = drop.author.handle;
    $[48] = t23;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t22 || $[52] !== t25) {
    t26 = <UserProfileTooltipWrapper user={t22}>{t25}</UserProfileTooltipWrapper>;
    $[51] = t22;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const t27 = drop.author.level || 0;
  let t28;
  if ($[54] !== t27) {
    t28 = <UserCICAndLevel level={t27} size={UserCICAndLevelSize.SMALL} />;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t21 || $[57] !== t26 || $[58] !== t28) {
    t29 = <div className="tw-mt-4 tw-flex tw-items-center tw-gap-2">{t21}{t26}{t28}</div>;
    $[56] = t21;
    $[57] = t26;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== drop) {
    t30 = <div onClick={_temp}><MyStreamWaveMyVoteVotes drop={drop} /></div>;
    $[60] = drop;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== drop.id || $[63] !== drop.top_raters) {
    let t32;
    if ($[65] !== drop.id) {
      t32 = voter => <React.Fragment key={voter.profile.id || voter.profile.primary_address}><Link href={`/${voter.profile.handle ?? voter.profile.primary_address}`} onClick={_temp2} data-tooltip-id={`my-vote-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`}>{voter.profile.pfp ? <img className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={voter.profile.pfp} alt="Recent voter" /> : <div className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link><Tooltip id={`my-vote-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`} place="top" offset={8} opacity={1} style={{
          padding: "4px 8px",
          background: "#37373E",
          color: "white",
          fontSize: "13px",
          fontWeight: 500,
          borderRadius: "6px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          zIndex: 99999,
          pointerEvents: "none"
        }}>{voter.profile.handle ?? voter.profile.primary_address}{" "}- {formatNumberWithCommas(voter.rating)}</Tooltip></React.Fragment>;
      $[65] = drop.id;
      $[66] = t32;
    } else {
      t32 = $[66];
    }
    t31 = drop.top_raters.slice(0, 3).map(t32);
    $[62] = drop.id;
    $[63] = drop.top_raters;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[67] !== t31) {
    t32 = <div className="tw-hidden tw-items-center -tw-space-x-2 sm:tw-flex">{t31}</div>;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== drop.raters_count) {
    t33 = formatNumberWithCommas(drop.raters_count);
    $[69] = drop.raters_count;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  const t34 = drop.raters_count === 1 ? "voter" : "voters";
  let t35;
  if ($[71] !== t34) {
    t35 = <span className="tw-font-normal tw-text-iron-500">{t34}</span>;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== t33 || $[74] !== t35) {
    t36 = <span className="tw-text-sm tw-font-semibold tw-text-iron-200">{t33}{" "}{t35}</span>;
    $[73] = t33;
    $[74] = t35;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== t32 || $[77] !== t36) {
    t37 = <div className="tw-flex tw-items-center tw-gap-2">{t32}{t36}</div>;
    $[76] = t32;
    $[77] = t36;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== t30 || $[80] !== t37) {
    t38 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-6 sm:tw-justify-start">{t30}{t37}</div>;
    $[79] = t30;
    $[80] = t37;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  let t39;
  if ($[82] !== drop || $[83] !== isResetting) {
    t39 = <div onClick={_temp3}><MyStreamWaveMyVoteInput drop={drop} isResetting={isResetting} /></div>;
    $[82] = drop;
    $[83] = isResetting;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== t38 || $[86] !== t39) {
    t40 = <div className="tw-mt-3.5 tw-flex tw-flex-col tw-justify-between tw-gap-4 xl:tw-mt-3 xl:tw-flex-row">{t38}{t39}</div>;
    $[85] = t38;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== t20 || $[89] !== t29 || $[90] !== t40) {
    t41 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">{t20}{t29}{t40}</div>;
    $[88] = t20;
    $[89] = t29;
    $[90] = t40;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  let t42;
  if ($[92] !== t13 || $[93] !== t15 || $[94] !== t41) {
    t42 = <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">{t13}{t15}{t41}</div>;
    $[92] = t13;
    $[93] = t15;
    $[94] = t41;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== drop.id || $[97] !== handleClick || $[98] !== t42 || $[99] !== t9) {
    t43 = <div key={t8} className={t9} onClick={handleClick}>{t42}</div>;
    $[96] = drop.id;
    $[97] = handleClick;
    $[98] = t42;
    $[99] = t9;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  return t43;
};
export default MyStreamWaveMyVote;
function _temp(e_1) {
  return e_1.stopPropagation();
}
function _temp2(e_2) {
  return e_2.stopPropagation();
}
function _temp3(e_3) {
  return e_3.stopPropagation();
}